import { useState, useCallback } from 'react';
import { useFeatureAccess } from './useFeatureAccess';
import { FeatureName } from '../types';

interface UseReadOnlyActionReturn {
  // Wrap an action - returns true if action was blocked, false if it can proceed
  handleAction: <T extends (...args: any[]) => any>(
    action: T
  ) => (...args: Parameters<T>) => ReturnType<T> | undefined;
  // Check if currently in read-only mode
  isReadOnly: boolean;
  // Show the work in progress message
  showWorkInProgress: () => void;
  // Snackbar state
  snackbarOpen: boolean;
  // Close snackbar
  closeSnackbar: () => void;
  // Message to display
  message: string;
}

/**
 * Hook for handling actions when in read-only mode
 *
 * Usage:
 * const { handleAction, snackbarOpen, closeSnackbar, message, isReadOnly } = useReadOnlyAction('trades');
 *
 * // Wrap any action
 * const handleSubmit = handleAction(async () => {
 *   await saveData();
 * });
 *
 * // In JSX, show snackbar
 * <Snackbar open={snackbarOpen} onClose={closeSnackbar}>
 *   <Alert>{message}</Alert>
 * </Snackbar>
 */
export function useReadOnlyAction(featureFlag: FeatureName): UseReadOnlyActionReturn {
  const { isReadOnlyMode, canPerformAction, getDisabledActionMessage } = useFeatureAccess();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const isReadOnly = isReadOnlyMode(featureFlag);
  const message = getDisabledActionMessage();

  const showWorkInProgress = useCallback(() => {
    setSnackbarOpen(true);
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const handleAction = useCallback(<T extends (...args: any[]) => any>(action: T) => {
    return (...args: Parameters<T>): ReturnType<T> | undefined => {
      if (isReadOnly || !canPerformAction(featureFlag)) {
        setSnackbarOpen(true);
        return undefined;
      }
      return action(...args);
    };
  }, [isReadOnly, canPerformAction, featureFlag]);

  return {
    handleAction,
    isReadOnly,
    showWorkInProgress,
    snackbarOpen,
    closeSnackbar,
    message,
  };
}

export default useReadOnlyAction;
